import { Icon, Icons } from '@aidexa/open-ds';
import { ClipboardEvent, ChangeEvent, MouseEvent, useEffect, useState, KeyboardEvent } from 'react';
import styles from './InputField.module.scss';

/* Fixme : replace with open-ds component */
export type Props = {
  capitalizeFlag?: boolean;
  changeValue?: (value: any, error: boolean) => any;
  onEnter?: (value?: any) => any;
  error?: boolean;
  flagForOnBlur?: boolean;
  id?: string;
  label?: string;
  maxLength?: number;
  message?: string;
  onBlur?: (value: any) => void;
  onChange?: (value: any) => void;
  onPaste?: (value: any) => void;
  onClick?: (value: any) => void;
  placeholder?: string;
  readOnly?: boolean;
  required?: boolean;
  type?: string;
  value?: string | number;
  upperCaseFlag?: boolean;
  autoComplete?: 'off';
  dataTestId?: string;
  disabled?: boolean;
  ariaDescribedby?: boolean;
};

const InputField = ({
  capitalizeFlag,
  id,
  flagForOnBlur = false,
  label,
  error,
  maxLength,
  message,
  onBlur,
  onChange,
  onClick,
  onPaste,
  onEnter,
  placeholder,
  type,
  value,
  readOnly,
  required,
  upperCaseFlag,
  autoComplete,
  dataTestId = '',
  disabled,
  ariaDescribedby
}: Props) => {
  const [touched, setTouched] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const maxLengthInput = maxLength ? maxLength : 100;

  useEffect(() => {
    if (!touched) {
      value && setInputValue(value?.toString());
    }
  }, [value]);

  const clickHandler = (e: MouseEvent) => {
    onClick && onClick(e);
  };

  const blurHandler = (e: any) => {
    setTouched(true);
    onBlur && onBlur(e);
  };

  const validationBasedOnLength = (value: string) => {
    return !!(maxLengthInput && value.length <= maxLengthInput);
  };

  const validationBasedOnNumber = (value: string) => {
    const validationRole = /^\d*$/;
    return !!value.match(validationRole);
  };

  const handleValidateEmail = (value: string) => {
    const emailRegex = /^[-a-z0-9~!@$%^&*_.=+#\/`|}{'?]+$/i;
    return value.match(emailRegex) ? true : false;
  };

  const handleValidateNoSpecialChar = (value: string) => {
    const noSpecialCharRegex = /^[A-Za-z0-9 .']+$/;
    return value.match(noSpecialCharRegex) ? true : false;
  };

  const handleValidatePartnerCode = (value: string) => {
    const validatePartnerCode = /^[A-Za-z0-9 -]+$/;
    return value.match(validatePartnerCode) ? true : false;
  };

  const handleValidateDiscountCode = (value: string) => {
    const validateDiscountCode = /^[A-Za-z0-9]+$/;
    return value.match(validateDiscountCode) ? true : false;
  };

  const handleOnChange = (inputValue: ChangeEvent<HTMLInputElement>) => {
    const value = inputValue.target.value;
    let validator;
    if (type === 'number' || type === 'tel') {
      validator = validationBasedOnNumber(value) && validationBasedOnLength(value);
    } else if (type === 'email') {
      validator = handleValidateEmail(value) && validationBasedOnLength(value);
    } else if (type === 'partnerCode') {
      validator = handleValidatePartnerCode(value) && validationBasedOnLength(value);
    } else if (type === 'discountCode') {
      validator = handleValidateDiscountCode(value) && validationBasedOnLength(value);
    } else if (type === 'input') {
      // In input type we allow all characters
      validator = validationBasedOnLength(value);
    } else {
      validator = handleValidateNoSpecialChar(value) && validationBasedOnLength(value);
    }

    setInputValue(value);

    if (validator || value === '') {
      onChange && onChange(value);
    }
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    event.key === 'Enter' && onEnter && onEnter(event);
  };

  const handleClipboardEvent = (e: ClipboardEvent<HTMLInputElement>) => {
    onPaste && onPaste(e.clipboardData.getData('Text'));
  };

  const getAriaDescribedBy = () => {
    if (ariaDescribedby) {
      return message;
    }
    if (message) {
      if (error) {
        return `${id}_error`;
      }
      return `${id}_help`;
    }
    if (error) {
      return `${id}_error`;
    }
    return undefined;
  };

  return (
    <div className={`open-field-input ${styles.field}`}>
      <label htmlFor={id}>{label}</label>
      {type === 'tel' && inputValue.length > 0 && (
        <label
          htmlFor={id}
          className={`${styles.prefix} ${inputValue.length > 8 ? styles.complitedNumber : styles.unComplitedNumber}`}
        >
          +39{' '}
        </label>
      )}
      <input
        title={placeholder || ''}
        aria-describedby={getAriaDescribedBy()}
        aria-invalid={error ? 'true' : undefined}
        className={`${type === 'tel' && inputValue.length > 0 ? styles.phoneInput : ''} ${
          capitalizeFlag ? styles.toCapitalizeInput : ''
        } ${upperCaseFlag ? styles.toUpperCaseInput : ''}`}
        id={id}
        data-testid={dataTestId || id || 'inputField'}
        inputMode={type === 'number' || type === 'tel' ? 'numeric' : undefined}
        maxLength={maxLengthInput}
        onClick={clickHandler}
        onChange={handleOnChange}
        onPaste={handleClipboardEvent}
        onBlur={blurHandler}
        pattern={type === 'number' || type === 'tel' ? '[0-9]*' : undefined}
        placeholder={placeholder}
        readOnly={readOnly}
        required={required}
        type="text"
        style={type === 'tel' && inputValue.length > 0 ? { paddingLeft: '3rem' } : undefined}
        value={inputValue || flagForOnBlur ? value : inputValue}
        autoComplete={autoComplete}
        onKeyDown={handleKeyDown}
        disabled={disabled}
      />
      {error ? (
        <div className={`${styles.formIcon} ${styles.errorIcon}`}>
          <Icon id={Icons.Warning} />
        </div>
      ) : null}
      {!!message && (
        <p
          data-testid={error ? 'inputError' : 'inputHint'}
          id={getAriaDescribedBy()}
          className={error ? undefined : 'open-helptext'}
          role={error ? 'alert' : undefined}
          aria-atomic="true"
        >
          {message}
        </p>
      )}
    </div>
  );
};

export default InputField;
